import { useState, useEffect, useRef } from 'react';

/**
 * Simple barcode scanner hook - based on proven working code from GS1TestComponent
 *
 * This is a simplified version that focuses on reliability over features.
 * It uses the exact same logic that works in the inline implementation.
 */

export interface SimpleBarcodeScannerOptions {
  /**
   * Callback when a barcode is scanned
   */
  onScan: (barcode: string) => void;

  /**
   * Whether the scanner is enabled
   * Default: true
   */
  enabled?: boolean;
}

export interface SimpleBarcodeScannerResult {
  isScanning: boolean;
  scanProgress: string;
  isEnabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

/**
 * Simple barcode scanner hook
 *
 * @example
 * const scanner = useSimpleBarcodeScanner({
 *   onScan: (barcode) => console.log('Scanned:', barcode),
 *   enabled: true
 * });
 */
export function useSimpleBarcodeScanner(
  options: SimpleBarcodeScannerOptions
): SimpleBarcodeScannerResult {
  const { onScan, enabled = true } = options;

  const [isEnabled, setEnabled] = useState(enabled);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');

  // Refs for scanner logic
  const scanBufferRef = useRef<string>('');
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastKeystrokeTimeRef = useRef<number>(0);

  // Store callback in ref to avoid recreating event listener
  const onScanRef = useRef(onScan);

  // Update callback ref when it changes
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  // Global keyboard event handler for scanner input
  useEffect(() => {
    if (!isEnabled) {
      // Clear any ongoing scan when disabled
      scanBufferRef.current = '';
      setScanProgress('');
      setIsScanning(false);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea/select element
      const target = event.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' ||
                          target.tagName === 'TEXTAREA' ||
                          target.tagName === 'SELECT';

      // If focus is on an input field, let it handle the input normally
      if (isInputField) {
        return;
      }

      // Allow browser shortcuts and modifier key combinations
      // This prevents blocking Ctrl+C, Ctrl+F, Cmd+C, etc.
      const hasModifier = event.ctrlKey || event.metaKey || event.altKey;
      if (hasModifier) {
        return;
      }

      // Check if user has text selected on the page
      // If text is selected, user likely wants to copy it, not scan a barcode
      const selection = window.getSelection();
      const hasTextSelected = selection && selection.toString().length > 0;
      if (hasTextSelected) {
        return;
      }

      // Allow F-keys to pass through (F1-F12 for browser functions)
      if (event.key.startsWith('F') && event.key.length >= 2 && event.key.length <= 3) {
        const fKeyNumber = parseInt(event.key.substring(1));
        if (!isNaN(fKeyNumber) && fKeyNumber >= 1 && fKeyNumber <= 12) {
          return;
        }
      }

      // Get the current timestamp
      const now = Date.now();
      const timeSinceLastKey = now - lastKeystrokeTimeRef.current;

      // Scanner typically sends characters very fast (< 50ms between chars)
      // If time between keystrokes is > 150ms, it's likely manual typing - reset buffer
      if (timeSinceLastKey > 150 && scanBufferRef.current.length > 0) {
        scanBufferRef.current = '';
        setScanProgress('');
        setIsScanning(false);
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
          scanTimeoutRef.current = null;
        }
      }

      lastKeystrokeTimeRef.current = now;

      // Handle Enter key - process the accumulated barcode
      if (event.key === 'Enter') {
        // Only preventDefault if we have a barcode buffer to process
        if (scanBufferRef.current.length > 0) {
          event.preventDefault();
          const barcode = scanBufferRef.current.trim();
          if (barcode) {
            onScanRef.current(barcode);
          }
          scanBufferRef.current = '';
          setScanProgress('');
          setIsScanning(false);
        }
        return;
      }

      // Handle Escape key - cancel scanning
      if (event.key === 'Escape') {
        // Only cancel if we're actually scanning
        if (scanBufferRef.current.length > 0) {
          scanBufferRef.current = '';
          setScanProgress('');
          setIsScanning(false);
          if (scanTimeoutRef.current) {
            clearTimeout(scanTimeoutRef.current);
            scanTimeoutRef.current = null;
          }
        }
        return;
      }

      // Ignore modifier keys, function keys, navigation keys, etc.
      if (event.key.length > 1 &&
          event.key !== 'Enter' &&
          event.key !== 'Escape') {
        return;
      }

      // Accumulate printable characters for barcode scanning
      if (event.key.length === 1) {
        // Now safe to prevent default - we've filtered out all browser shortcuts
        event.preventDefault();

        scanBufferRef.current += event.key;
        setIsScanning(true);
        setScanProgress(scanBufferRef.current);

        // Clear any existing timeout
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
        }

        // Set timeout to process barcode if no more input comes
        // Scanners typically finish in < 100ms, so 150ms timeout is safe
        scanTimeoutRef.current = setTimeout(() => {
          if (scanBufferRef.current.length > 0) {
            const barcode = scanBufferRef.current.trim();
            if (barcode) {
              onScanRef.current(barcode);
            }
            scanBufferRef.current = '';
            setScanProgress('');
            setIsScanning(false);
          }
        }, 150);
      }
    };

    // Attach the event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, [isEnabled]); // Only re-run when isEnabled changes

  return {
    isScanning,
    scanProgress,
    isEnabled,
    setEnabled,
  };
}
